import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Wand2, Save, Sparkles, Lock, Info, ArrowRightLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { api } from '../../api/feedy';
import { saveCollection } from '../../utils/saveCollection';

const newRow = (order, preset = {}) => ({
  target_field: '', source_type: 'field', source_value: '', enabled: true, order, ...preset,
});

// On a non-EUR feed the cost_of_goods_sold VALUE is intentionally the bare EUR
// cost ({v_product_cost}); an auto FX rule (backend feeds/fx.py) multiplies it by
// the market rate and appends the currency at export. Detect that rule (tagged
// auto:'cogs_fx') and return its target currency, so the row can say "reworked to
// <CCY>" instead of looking like it's missing its currency. Null when there's no
// such rule (EUR feeds already show "{v_product_cost} EUR").
function cogsFxCurrency(feed) {
  for (const r of feed?.rules || []) {
    const actions = Array.isArray(r.action) ? r.action : [r.action];
    if (!actions.some((a) => a && a.auto === 'cogs_fx')) continue;
    const setA = actions.find((a) => a && a.type === 'set' && typeof a.value === 'string');
    const m = setA && setA.value.match(/\}\s*([A-Z]{3})\s*$/);
    return (m && m[1]) || feed.currency || '';
  }
  return null;
}

function shippingFxCurrency(feed) {
  return (feed?.currency || '').trim().toUpperCase() || null;
}

// Plain-language "how it's generated" for auto fields (falls back to the channel
// field's own description for any auto field not listed here).
const AUTO_HELP = {
  product_detail: 'Generated at export from your product specs — nothing to map. For each product we pick the specs relevant to its Google category (bulbs → wattage, lumens, colour temperature, socket; fixtures → IP rating, material, socket, dimensions; furniture → material, dimensions, origin; …), take each value from the most specific source available (variant v_ → product p_ → Shopify metafield), and emit a <g:product_detail> block per spec with the labels in the feed’s language. It follows the Categorisation rules + your synced specs.',
  identifier_exists: 'Emitted automatically as “no” only when a product has neither a GTIN (barcode) nor an MPN. That tells Google the missing identifier is intentional, so it drops the missing-identifier penalty. When a GTIN or MPN is present, nothing is emitted (Google assumes “yes”).',
};

export default function MappingTab({ feed, channelFields, internalFields, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openAuto, setOpenAuto] = useState(null);
  const originalIds = useRef([]);
  const autoFields = channelFields.filter((f) => f.auto);
  const cogsCcy = cogsFxCurrency(feed);
  const shippingCcy = shippingFxCurrency(feed);

  useEffect(() => {
    // Show mappings in the channel's canonical field order — the same order the
    // XML emits (Required → Identifiers → Categorisation → … → Custom labels).
    // Custom/unknown targets sink to the end, keeping their saved order.
    const canonIndex = (name) => {
      const i = channelFields.findIndex((f) => f.name === name);
      return i < 0 ? 1e6 : i;
    };
    const init = [...(feed.mappings || [])]
      .sort((a, b) => (canonIndex(a.target_field) - canonIndex(b.target_field)) || (a.order - b.order))
      .map((m) => ({ ...m }));
    setRows(init);
    originalIds.current = init.filter((m) => m.id).map((m) => m.id);
  }, [feed.id, channelFields.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () => setRows((r) => [...r, newRow(r.length)]);

  const autoMap = () => {
    const mapped = new Set(rows.map((r) => r.target_field));
    const internalNames = new Set(internalFields.map((f) => f.name));
    const additions = [];
    channelFields.forEach((cf) => {
      if (mapped.has(cf.name)) return;
      if (cf.required || cf.recommended || internalNames.has(cf.name)) {
        additions.push(newRow(0, {
          target_field: cf.name,
          source_value: internalNames.has(cf.name) ? cf.name : '',
        }));
      }
    });
    setRows((r) => [...r, ...additions.map((a, i) => ({ ...a, order: r.length + i }))]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveCollection(rows, originalIds.current, {
        payload: (r, idx) => ({
          feed: feed.id, target_field: r.target_field, source_type: r.source_type,
          source_value: r.source_value, order: idx, enabled: r.enabled,
        }),
        create: api.mappings.create, update: api.mappings.update, remove: api.mappings.remove,
      });
      setRows(res.rows);
      originalIds.current = res.ids;
      if (!res.error) await onSaved();  // failures were already shown as a toast
    } finally {
      setSaving(false);
    }
  };

  const requiredNames = channelFields.filter((f) => f.required).map((f) => f.name);
  const mappedNames = new Set(rows.map((r) => r.target_field));
  const missingRequired = requiredNames.filter((n) => !mappedNames.has(n));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Map each channel field to an internal field, a static value, or a <code>{'{template}'}</code>.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={autoMap}><Wand2 className="h-4 w-4" /> Auto-map</Button>
          <Button size="sm" className="gap-1" onClick={save} disabled={saving}><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save mapping'}</Button>
        </div>
      </div>

      {missingRequired.length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          Missing required fields: {missingRequired.map((n) => <code key={n} className="mx-0.5">{n}</code>)}
        </div>
      )}

      <div className="rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[26%]">Channel field</TableHead>
              <TableHead className="w-[18%]">Source</TableHead>
              <TableHead>Value</TableHead>
              <TableHead className="w-[80px] text-center">On</TableHead>
              <TableHead className="w-[48px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No mappings yet. Use <strong>Auto-map</strong> to get started.</TableCell></TableRow>
            )}
            {rows.map((row, i) => {
              const spec = channelFields.find((f) => f.name === row.target_field);
              return (
                <TableRow key={row.id ?? `new-${i}`}>
                  <TableCell>
                    <Select value={row.target_field} onChange={(e) => update(i, { target_field: e.target.value })} className="h-9">
                      <option value="">— select —</option>
                      {channelFields.filter((f) => !f.auto).map((f) => (
                        <option key={f.name} value={f.name}>{f.name}{f.required ? ' *' : ''}</option>
                      ))}
                      {row.target_field && !spec && <option value={row.target_field}>{row.target_field}</option>}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={row.source_type} onChange={(e) => update(i, { source_type: e.target.value })} className="h-9">
                      <option value="field">Field</option>
                      <option value="static">Static</option>
                      <option value="template">Template</option>
                      <option value="blank">Blank</option>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {row.source_type === 'blank' ? (
                      <span className="text-xs italic text-muted-foreground">— always empty (emits an empty tag) —</span>
                    ) : row.source_type === 'field' ? (
                      <Select value={row.source_value} onChange={(e) => update(i, { source_value: e.target.value })} className="h-9">
                        <option value="">— select field —</option>
                        {internalFields.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                        {row.source_value && !internalFields.some((f) => f.name === row.source_value) && (
                          <option value={row.source_value}>{row.source_value}</option>
                        )}
                      </Select>
                    ) : (
                      <Input
                        value={row.source_value}
                        onChange={(e) => update(i, { source_value: e.target.value })}
                        className="h-9"
                        placeholder={row.source_type === 'template' ? '{brand} {title}' : 'new'}
                      />
                    )}
                    {row.target_field === 'cost_of_goods_sold' && cogsCcy && (
                      <div className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
                        <ArrowRightLeft className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                        <span>
                          Reworked to <strong>{cogsCcy}</strong> at export — the bare value is the EUR cost;
                          the “COGS: EUR → {cogsCcy}” rule converts it at the market rate (refreshed nightly).
                        </span>
                      </div>
                    )}
                    {row.target_field === 'shipping_price' && shippingCcy && (
                      <div className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
                        <ArrowRightLeft className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                        <span>
                          Reworked to <strong>{shippingCcy}</strong> at export — the shipping matrix stores EUR rates;
                          Google export converts and formats the nested shipping price for this feed currency.
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={row.enabled} onCheckedChange={(v) => update(i, { enabled: v })} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => removeRow(i)} aria-label="Remove"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Auto-generated fields: shown for visibility but not editable — the
                channel derives them at export time. Click the info icon to see how. */}
            {autoFields.length > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="bg-muted/40 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Auto-generated · created at export, not mappable
                </TableCell>
              </TableRow>
            )}
            {autoFields.map((f) => (
              <React.Fragment key={`auto-${f.name}`}>
                <TableRow className="bg-muted/10">
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                      <code className="text-xs font-medium">{f.name}</code>
                      {f.multi && <Badge variant="outline" className="text-[10px]">repeatable</Badge>}
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs italic text-muted-foreground">auto</span></TableCell>
                  <TableCell><span className="text-xs italic text-muted-foreground">generated at export</span></TableCell>
                  <TableCell className="text-center"><Lock className="mx-auto h-3.5 w-3.5 text-muted-foreground" /></TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setOpenAuto(openAuto === f.name ? null : f.name)}
                      aria-label={`How ${f.name} is generated`}
                      aria-expanded={openAuto === f.name}
                    >
                      <Info className={`h-4 w-4 ${openAuto === f.name ? 'text-violet-500' : ''}`} />
                    </Button>
                  </TableCell>
                </TableRow>
                {openAuto === f.name && (
                  <TableRow className="bg-muted/5">
                    <TableCell colSpan={5} className="py-3 text-xs leading-relaxed text-muted-foreground">
                      {AUTO_HELP[f.name] || f.description}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <Button variant="outline" size="sm" className="gap-1" onClick={addRow}><Plus className="h-4 w-4" /> Add field</Button>
    </div>
  );
}
